import { ProjectUser } from '@monorepo/shared/apiClient/types';
import { ActionMenu } from '@monorepo/shared/componentsV2/menus/ActionMenu';
import { MenuOption } from '@monorepo/shared/componentsV2/menus/Menu';
import {
  useHasOrganizationUpdatePermissions,
  useHasProjectReassignTasksPermissions,
} from '@monorepo/shared/hooks/permissions/useHasPermissions';
import React, { useMemo, useState } from 'react';
import { useConfirmRemoveUserFromProject } from '../hooks/useConfirmRemoveUserFromProject';
import { useConfirmSetUserIsOrganizationAdmin } from '../hooks/useConfirmSetUserIsOrganizationAdmin';
import { useResendUserInvite } from '../hooks/useResendUserInvite';
import { ChangeUserRoleModal } from './ChangeUserRoleModal';
import {
  getChangeRoleAction,
  getChangeRoleOptionsForUser,
} from './changeUserRoleUtils';
import { getNotificationSettingsAction } from './notificationSettingsUtils';
import { getOrgAdminAction } from './orgAdminActionUtils';
import { ReassignTasksModal } from './ReassignTasksModal';
import { getReassignTasksAction } from './reassignTasksUtils';
import { getRemoveFromSiteAction } from './removeFromSiteUtils';
import { getResendInviteAction } from './resendInviteUtils';
import { isOrgAdmin } from './sharedUtils';

interface UsersTableActionsProps {
  organizationId: string;
  projectId: string;
  user: ProjectUser;
  currentUser: ProjectUser;
  canManageUsers: boolean;
}

export function UsersTableUserActions({
  organizationId,
  projectId,
  user,
  currentUser,
  canManageUsers,
}: UsersTableActionsProps) {
  const [isChangeRoleModalOpen, setIsChangeRoleModalOpen] = useState(false);
  const [isReassignTasksModalOpen, setIsReassignTasksModalOpen] =
    useState(false);
  const isSelf = currentUser?.userId === user.userId;

  const canManageOrgAdmins =
    useHasOrganizationUpdatePermissions(organizationId);
  const canReassignTasks = useHasProjectReassignTasksPermissions(projectId);

  const handleRemove = useConfirmRemoveUserFromProject({
    projectId,
    userId: user.userId,
    userName: user.userName,
  });

  const handleSetOrgAdmin = useConfirmSetUserIsOrganizationAdmin({
    organizationId,
    projectId,
    userId: user.userId,
    userName: user.userName,
    isSettingOrgAdmin: !isOrgAdmin(user),
  });

  const handleResendInvite = useResendUserInvite({
    projectId,
    userId: user.userId,
    userName: user.userName,
    role: user.projectRoles ?? '',
  });

  const { roleOptions, disabledTooltip: changeRoleDisabledTooltip } = useMemo(
    () => getChangeRoleOptionsForUser(currentUser, user, canManageUsers),
    [currentUser, user, canManageUsers],
  );

  const menuOptions = useMemo(() => {
    const options: MenuOption[] = [
      getNotificationSettingsAction(currentUser, user, canManageUsers),
      getChangeRoleAction({
        disabledTooltip: changeRoleDisabledTooltip,
        onChangeRole: () => setIsChangeRoleModalOpen(true),
      }),
      getResendInviteAction({
        user,
        isSelf,
        canManageUsers,
        onResend: handleResendInvite,
      }),
      ...(canManageOrgAdmins
        ? [
            getOrgAdminAction({
              user,
              isSelf,
              onSetOrgAdmin: handleSetOrgAdmin,
            }),
          ]
        : []),
      getReassignTasksAction({
        canReassignTasks,
        onReassignTasks: () => setIsReassignTasksModalOpen(true),
      }),
      getRemoveFromSiteAction({
        user,
        isSelf,
        canManageUsers,
        onRemove: handleRemove,
      }),
    ];

    return options;
  }, [
    currentUser,
    user,
    canManageUsers,
    canManageOrgAdmins,
    canReassignTasks,
    changeRoleDisabledTooltip,
    isSelf,
    handleResendInvite,
    handleRemove,
    handleSetOrgAdmin,
  ]);

  return (
    <>
      <ActionMenu
        altText={`Action menu for ${user.userName}`}
        options={menuOptions}
      />
      {isChangeRoleModalOpen && user.projectRoles && (
        <ChangeUserRoleModal
          projectId={projectId}
          user={user}
          currentRole={user.projectRoles}
          roleOptions={roleOptions}
          isSelf={isSelf}
          onClose={() => setIsChangeRoleModalOpen(false)}
        />
      )}
      {isReassignTasksModalOpen && (
        <ReassignTasksModal
          organizationId={organizationId}
          projectId={projectId}
          user={user}
          onClose={() => setIsReassignTasksModalOpen(false)}
        />
      )}
    </>
  );
}
